import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate  } from "react-router-dom";
import OrderItem from "../../components/Order/OrderItem";
import OrderPayment from "../../components/Order/OrderPayment";
import DaumPostcodeEmbed from 'react-daum-postcode';
import AddressSelectPopup from "../../components/Popup/AddressSelectPopup";
import orderApi from "../../services/Order/orderApi";
import PaymentService from "../../services/Payment/PaymentService";
import { debounce } from 'lodash';
import { formatLocalString } from "../../utils/validation";
import { useLanguage } from "../../hooks/useLanguage";
import useUserStore from "../../store/userStore";
import PopupLoginError from "../../components/Popup/LoginError";
import { useOrderCount } from './OrderView'; // Context import 추가
import TermsPopup from "../../components/Popup/TermsPopup";
import { TERMS_LIST_URL, FOOTER_DATA_URL } from "../../utils/urls";
import instance from "../../services/services";

// 디바운스된 API 호출 함수 생성
const debouncedFn = debounce((callback) => {
  callback();
}, 300);

function SaleOrder(){
  const { t } = useLanguage();
  const { memberTokens, userInfo } = useUserStore();
  const navigate = useNavigate();
  
  // Context 사용 추가
  const { refreshOrderCount } = useOrderCount();

  const [memo, setMemo] = useState('');
  const [selectedDelivery, setSelectedDelivery] = useState('default'); //배송지 선택
  const [selectedMessage, setSelectedMessage] = useState(''); // 배송 요청사항
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [isPopupAddr,setPopupAddr] = useState(false);
  const [deliveryData, setDeliveryData] = useState([]);
  const [isPopupLoginError, setIsPopupLoginError] = useState("");

  // 우편번호 관련 상태 추가
  const [isPostcodeOpen, setIsPostcodeOpen] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState({
    zonecode: '',
    roadAddress: '',
    detailAddress: ''
  });

  const [recipientInfo, setRecipientInfo] = useState({
    name: '',
    phone: ''
  });

  // 상태 추가
  const [cartListData, setCartListData] = useState([]);
  const [orderSummary, setOrderSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [isPayment, setIsPayment] = useState(false);
  const [requestParams, setRequestParams] = useState({
    point: "",
    allPointUse: "N",
    coupon_seq: "",
    balance: "",
    allBalanceUse: "N",
    maxCouponUse: "N",
    download_seq: "",
    key: Date.now()
  });

  // useRef 추가
  const selectedProductIdsRef = useRef([]);
  const didSetInitialSelection = useRef(false);

  // selectedProductIds가 변경될 때마다 ref 업데이트
  useEffect(() => {
    selectedProductIdsRef.current = selectedProductIds;
  }, [selectedProductIds]);

  // 초기 데이터 로딩 시 상품 선택 처리
  useEffect(() => {
    if (
      !didSetInitialSelection.current && 
      cartListData && 
      Object.keys(cartListData).length > 0 && 
      selectedProductIds.length === 0
    ) {
      const main = cartListData.bundleMain || [];
      const add = cartListData.bundleAdd || [];
      const acc = cartListData.bundleAcc || [];

      const mainIds = main.map(item => item.cart_option_seq);
      const addIds = add.map(item => item.cart_option_seq);
      const accIds = acc.map(item => item.cart_option_seq);

      setCartListData({
        ...cartListData,
        bundleMain: main.map(item => ({ ...item, isChecked: true })),
        bundleAdd: add.map(item => ({ ...item, isChecked: true })),
        bundleAcc: acc.map(item => ({ ...item, isChecked: true }))
      });

      setSelectedProductIds([...mainIds, ...addIds, ...accIds]);
  

      didSetInitialSelection.current = true;
    }
  }, [cartListData, selectedProductIds.length]);

  // 배송지 선택 핸들러 수정
  const handleDeliveryChange = (e) => {
    const newValue = e.target.value;
    setSelectedDelivery(newValue);
    
    // '새로입력' 선택 시 모든 필드 초기화
    if (newValue === 'new') {
      setDeliveryAddress({
        zonecode: '',
        roadAddress: '',
        detailAddress: ''
      });
      
      setRecipientInfo({
        name: '',
        phone: ''
      });
      
      setSelectedMessage('');
      setCustomMessage('');
    } else {
      // 기본주소 선택 시 기존 배송지 정보로 복원
      setDeliveryAddress({
        zonecode: deliveryData.recipient_zipcode || '',
        roadAddress: deliveryData.recipient_address_street || '',
        detailAddress: deliveryData.recipient_address_detail || ''
      });
      
      setRecipientInfo({
        name: deliveryData.recipient_name || '',
        phone: deliveryData.recipient_phone || ''
      });
    }
  };

  // 우편번호 찾기 핸들러
  const handlePostcodeOpen = () => {
    setIsPostcodeOpen(true);
  };

  const handlePostcodeComplete = (data) => {
    setDeliveryAddress({
      zonecode: data.zonecode,
      roadAddress: data.roadAddress,
      detailAddress: ''
    });
    setIsPostcodeOpen(false);
  };

  const handlePostcodeClose = () => {
    setIsPostcodeOpen(false);
  };

  // 배송지 정보
  const deliveryLIst = [
    {id:'default_addr', label:t('default_address'), value:'default'},
    {id:'new_addr', label:t('input_new_address'), value:'new'}
  ]
  
  // 배송 요청사항
  const messageList = [
    { name:'부재 시 문 앞에 놓아주세요.', value:'reason1' },
    { name:'부재 시 경비실에 맡겨주세요.', value:'reason2' },
    { name:'부재 시 전화 또는 문자 주세요.', value:'reason3' },
    { name:'파손의 위험이 있는 상품입니다. 배송 시 주의해 주세요.', value:'reason4' },
    { name:'배송 전에 연락 주세요.', value:'reason5' },
    { name:'직접입력', value:'reason6' },
  ]

  // 요청사항
  const handleMemoChange = (e) => {
    setMemo(e.target.value);
  };

  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isChecked, setIsChecked] = useState({
    option1: false,
    option2: false,
    option3: false,
    option4: false,
  });

  const handleAllCheck = () => {
    setIsAllChecked(!isAllChecked);
    const newCheckedState = {
      option1: !isAllChecked,
      option2: !isAllChecked,
      option3: !isAllChecked,
      option4: !isAllChecked,
    };
    setIsChecked(newCheckedState);
  };

  const handleCheck = (option) => {
    const newCheckedState = { ...isChecked, [option]: !isChecked[option] };
    setIsChecked(newCheckedState);
    setIsAllChecked(Object.values(newCheckedState).every(Boolean));
  };

  const handleAddrSelectPopup= () => {
    setPopupAddr(true);
  }

    // 배송지 선택 핸들러 추가
    const handleAddressConfirm = (selectedAddress) => {
      if (selectedAddress) {
        // 기본 주소로 설정
        setSelectedDelivery('default');
        
        // deliveryData 상태 업데이트
        setDeliveryData({
          ...deliveryData,
          recipient_name: selectedAddress.recipient_name,
          recipient_phone: selectedAddress.recipient_phone,
          recipient_zipcode: selectedAddress.recipient_zipcode,
          recipient_address_street: selectedAddress.recipient_address_street,
          recipient_address_detail: selectedAddress.recipient_address_detail
        });
  
        // 기존 deliveryAddress와 recipientInfo 상태도 함께 업데이트
        setDeliveryAddress({
          zonecode: selectedAddress.recipient_zipcode,
          roadAddress: selectedAddress.recipient_address_street,
          detailAddress: selectedAddress.recipient_address_detail
        });
  
        setRecipientInfo({
          name: selectedAddress.recipient_name,
          phone: selectedAddress.recipient_phone
        });
      }
    };

  // API 호출 관련 함수들 추가
  const createRequestParams = useCallback((selectedIds) => {
    return {
      order_kind: "sale",  
      sale_order_kind : "online",            
      point: requestParams.point || "",                       
      allPointUse: requestParams.allPointUse || "N",                 
      download_seq: requestParams.download_seq || "",
      balance: requestParams.balance || "",                
      allBalanceUse: requestParams.allBalanceUse || "N",  
      cart_select_goods: selectedIds || selectedProductIds,            
      pay_select_goods: [],             
      undecidedChk: "N",
      maxCouponUse: requestParams.maxCouponUse || "N"
    };
  }, [selectedProductIds, requestParams]);

  const handleUpdateParams = useCallback((updates) => {
    setRequestParams((prev) => {
      const updated = { ...prev, ...updates };
  
      // API 호출
      const apiParams = {
        ...createRequestParams(updated.cart_select_goods),
        ...updated,
      };
  
      debouncedFn(() => {
        orderApi.getCartDetail(apiParams)
          .then((response) => {
            if (response.data.code === 200) {
              const data = response.data.data;
              setCartListData({
                bundleMain: data.cartList?.bundleMain?.map(item => ({
                  ...item,
                  isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
                })) || [],
                bundleAdd: data.cartList?.bundleAdd?.map(item => ({
                  ...item,
                  isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
                })) || [],
                bundleAcc: data.cartList?.bundleAcc?.map(item => ({
                  ...item,
                  isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
                })) || [],
              });
              setOrderSummary(data);
            }
          })
          .catch(error => {
            console.error('파라미터 업데이트 중 오류:', error);
          });
      });
  
      return updated;
    });
  }, [createRequestParams]);

  // 중앙화된 API 호출 함수
  const fetchCartDetail = useCallback((params = createRequestParams()) => {
    debouncedFn(() => {
      orderApi.getCartDetail(params)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;
            setCartListData({
              bundleMain: data.cartList.bundleMain?.map(item => ({
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
              bundleAdd: data.cartList.bundleAdd?.map(item => ({
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
              bundleAcc: data.cartList.bundleAcc?.map(item => ({
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || []
            });
            setOrderSummary(data);
          }
        })
        .catch(error => {
          console.error('API 호출 중 오류:', error);
        });
    });
  }, [createRequestParams]);

  // 초기 데이터 로딩을 위한 단일 useEffect
  useEffect(() => {
    if (!didSetInitialSelection.current) {
      setIsLoading(true);
      const params = createRequestParams();
      orderApi.getCartDetail(params)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;

            const mainIds = data.cartList.bundleMain?.map(item => item.cart_option_seq) || [];
            const addIds = data.cartList.bundleAdd?.map(item => item.cart_option_seq) || [];
            const accIds = data.cartList.bundleAcc?.map(item => item.cart_option_seq) || [];
            const allIds = [...mainIds, ...addIds, ...accIds];

            setSelectedProductIds(allIds);
            setCartListData({
              bundleMain: data.cartList.bundleMain?.map(item => ({ ...item, isChecked: true })) || [],
              bundleAdd: data.cartList.bundleAdd?.map(item => ({ ...item, isChecked: true })) || [],
              bundleAcc: data.cartList.bundleAcc?.map(item => ({ ...item, isChecked: true })) || [],
            });
            setOrderSummary(data);
            didSetInitialSelection.current = true;
          }
        })
        .catch(error => console.error('초기 데이터 로드 오류:', error))
        .finally(() => setIsLoading(false));
    }
  }, [createRequestParams]);

  // selectedProductIds 변경 시 API 호출
  useEffect(() => {
    if (didSetInitialSelection.current) {
      fetchCartDetail();
    }
  }, [fetchCartDetail]);

  // 상품 선택 핸들러 단순화
  const handleProductSelect = useCallback((productId, isSelected) => {
    setSelectedProductIds(prev => {
      const updated = isSelected
        ? [...new Set([...prev, productId])]
        : prev.filter(id => id !== productId);

      selectedProductIdsRef.current = updated;
      return updated;
    });
  }, []);

  // 상품 수량 변경 핸들러 수정
  const handleProductQuantityChange = useCallback((changeData) => {
    const params = {
      ...createRequestParams(),
      cart_change_goods: [changeData]
    };
    fetchCartDetail(params);
  }, [createRequestParams, fetchCartDetail]);

  // 상품 삭제 핸들러 수정
  const handleProductDelete = (deleteData) => {
    // 삭제될 상품 ID
    const deletedProductId = deleteData.cart_option_seq;
    
    // UI에서 먼저 삭제 (즉시 삭제 효과를 보여주기 위함)
    setCartListData(prev => ({
      bundleMain: prev.bundleMain?.filter(item => item.cart_option_seq !== deletedProductId) || [],
      bundleAdd: prev.bundleAdd?.filter(item => item.cart_option_seq !== deletedProductId) || [],
      bundleAcc: prev.bundleAcc?.filter(item => item.cart_option_seq !== deletedProductId) || []
    }));
    
    // selectedProductIds에서도 삭제
    const updatedProductIds = selectedProductIds.filter(id => id !== deletedProductId);
    setSelectedProductIds(updatedProductIds);
    
    // ref도 즉시 업데이트
    selectedProductIdsRef.current = updatedProductIds;
    
    // 체크박스 상태 초기화 - OrderPayment 컴포넌트의 체크박스 초기화
    setRequestParams(prev => ({
      ...prev,
      point: '',
      allPointUse: 'N',
      balance: '',
      allBalanceUse: 'N',
      maxCouponUse: 'N',
      download_seq: '',
      key: Date.now() // 키 업데이트로 컴포넌트 강제 리렌더링
    }));
    
    // API 호출 파라미터
    const params = {
      ...createRequestParams(updatedProductIds),
      cart_change_goods: [deleteData],
      // 체크박스 초기화 상태를 API 호출에도 포함
      point: '',
      allPointUse: 'N',
      balance: '',
      allBalanceUse: 'N',
      maxCouponUse: 'N',
      download_seq: ''
    };

    // API 호출
    orderApi.getCartDetail(params)
      .then(response => {
        // 장바구니가 비어있는 경우(코드가 -200)
        if (response.data.code === -200) {
          // 장바구니가 비었으므로 모든 데이터 초기화
          setCartListData({
            bundleMain: [],
            bundleAdd: [],
            bundleAcc: []
          });
          setSelectedProductIds([]);
          selectedProductIdsRef.current = [];
          setOrderSummary({...response.data.data, cartList: {bundleMain: [], bundleAdd: [], bundleAcc: []}});
          
          // 주문 개수 업데이트
          refreshOrderCount();
          return;
        }
        
        if (response.data.code === 200) {
          const data = response.data.data;
          
          // 서버 데이터로 상태 업데이트
          setCartListData({
            bundleMain: data.cartList?.bundleMain?.map(item => ({
              ...item,
              isChecked: updatedProductIds.includes(item.cart_option_seq)
            })) || [],
            bundleAdd: data.cartList?.bundleAdd?.map(item => ({
              ...item,
              isChecked: updatedProductIds.includes(item.cart_option_seq)
            })) || [],
            bundleAcc: data.cartList?.bundleAcc?.map(item => ({
              ...item,
              isChecked: updatedProductIds.includes(item.cart_option_seq)
            })) || []
          });
          
          // 주문 요약 정보 업데이트
          setOrderSummary(data);
          
          // 주문 개수 업데이트
          refreshOrderCount();
        }
      })
      .catch(error => {
        console.error('상품 삭제 중 오류:', error);
        // 오류 발생 시에도 UI 초기화
        setCartListData({
          bundleMain: [],
          bundleAdd: [],
          bundleAcc: []
        });
        setSelectedProductIds([]);
        selectedProductIdsRef.current = [];
        
        // 주문 개수 업데이트
        refreshOrderCount();
      });
  };

  // OrderItem 컴포넌트에서 호출하는 삭제 헬퍼 함수
  const handleDelete = (cartOptionSeq) => {
    // cartOptionSeq가 객체인 경우 (이미 잘 구성된 deleteData인 경우)
    if (typeof cartOptionSeq === 'object' && cartOptionSeq.cart_option_seq) {
      handleProductDelete(cartOptionSeq);
    } else {
      // cartOptionSeq가 문자열인 경우 (ID만 전달된 경우)
      const deleteData = {
        cart_option_seq: cartOptionSeq,
        ea: 0,
        delete_flag: 'Y'
      };
      handleProductDelete(deleteData);
    }
  };

  // 선택 상품 삭제 핸들러 수정
  const handleSelectedDelete = useCallback(() => {
    // cartListData에서 모든 상품을 하나의 배열로 합치기
    const allProducts = [
      ...(cartListData.bundleMain || []),
      ...(cartListData.bundleAdd || []),
      ...(cartListData.bundleAcc || [])
    ];

    const deleteList = selectedProductIds.map(id => {
      // cartListData에서 해당 cart_option_seq를 가진 상품 찾기
      const product = allProducts.find(item => item.cart_option_seq === id);
      // 상품을 찾으면 해당 ea 값 사용, 없으면 1 (기본값)
      const ea = product?.ea || 1;
      
      return {
        cart_option_seq: id,
        ea: ea,
        delete_flag: 'Y'
      };
    });

    const params = {
      ...createRequestParams(),
      cart_change_goods: deleteList
    };

    setSelectedProductIds([]);
    setIsAllChecked(false);
    
    // 체크박스 상태 초기화 - OrderPayment 컴포넌트의 체크박스 초기화
    setRequestParams(prev => ({
      ...prev,
      point: '',
      allPointUse: 'N',
      balance: '',
      allBalanceUse: 'N',
      maxCouponUse: 'N',
      download_seq: '',
      key: Date.now() // 키 업데이트로 컴포넌트 강제 리렌더링
    }));
    
    orderApi.getCartDetail(params)
      .then(res => {
        // 장바구니가 비어있는 경우
        if (res.data.code === -200) {
          setCartListData({
            bundleMain: [],
            bundleAdd: [],
            bundleAcc: []
          });
          selectedProductIdsRef.current = [];
          setOrderSummary({...res.data.data, cartList: {bundleMain: [], bundleAdd: [], bundleAcc: []}});
          
          // 주문 개수 업데이트
          refreshOrderCount();
          return;
        }
        
        if (res.data.code === 200) {
          fetchCartDetail(params);
          
          // 주문 개수 업데이트
          refreshOrderCount();
        }
      })
      .catch(err => {
        console.error('선택상품 삭제 실패', err);
        // 오류 발생 시에도 UI 초기화
        setCartListData({
          bundleMain: [],
          bundleAdd: [],
          bundleAcc: []
        });
        selectedProductIdsRef.current = [];
        
        // 주문 개수 업데이트
        refreshOrderCount();
      });
  }, [selectedProductIds, cartListData, createRequestParams, fetchCartDetail, refreshOrderCount]);

  // 결제 관련 함수 추가
  const orderInfoValidation = () => {
    const validation = PaymentService.validatePaymentForm({
      deliveryInfo: {
        recipient_zipcode: selectedDelivery === 'default' ? 
          deliveryData.recipient_zipcode : 
          deliveryAddress.zonecode,
        recipient_address_street: selectedDelivery === 'default' ? 
          deliveryData.recipient_address_street : 
          deliveryAddress.roadAddress,
      }
    });
    
    if (!validation.isValid) {
      setIsPopupLoginError(validation.errors[0]);
      return false;
    }
    
    return true;
  };

  const handlePayment = async () => {
    if (!selectedProductIds || selectedProductIds.length === 0) {
      setIsPopupLoginError(t('select_payment_product'));
      return;
    }

    if (!Object.values(isChecked).every(Boolean)) {
      setIsPopupLoginError(t('agree_required_terms'));
      return;
    }

    const isValid = orderInfoValidation();
    if (!isValid) return;

    // 결제 요청 데이터 준비
    const paymentForm = {
      order_seq: "",                
      reorder_chk: "N",            
      order_type: "sale",    
      sale_order_kind : "online",    
      point: requestParams.point || "",                
      balance: requestParams.balance || "",
      download_seq: requestParams.maxCouponUse === 'Y' 
      ? orderSummary?.totalInfo?.maxDiscountCouponSeq 
      : requestParams.download_seq || "", 
      shipping_cost: orderSummary?.totalInfo?.delivery_price || 0,
      balance_cash_receipt: requestParams.deposit_cash_receipt || 'N',   
      balance_cash_num: requestParams.deposit_cash_num || '',      // 현금영수증 번호
      
      // 할인 정보
      discountInfo: {
        first_payment_sale: orderSummary?.totalInfo?.first_payment_sale || 0,
        mem_group_sale: orderSummary?.totalInfo?.sumGroupSale || 0,
        mem_type_sale: orderSummary?.totalInfo?.sumMtypeSale || 0
      },
      
      // 배송 정보 수정
      deliveryInfo: {
        recipient_name: selectedDelivery === 'default' ? 
          deliveryData.recipient_name : 
          recipientInfo.name,
        recipient_phone: selectedDelivery === 'default' ? 
          deliveryData.recipient_phone : 
          recipientInfo.phone,
        // 배송지 정보 수정
        recipient_zipcode: selectedDelivery === 'default' ? 
          deliveryData.recipient_zipcode : 
          deliveryAddress.zonecode,
        recipient_address_street: selectedDelivery === 'default' ? 
          deliveryData.recipient_address_street : 
          deliveryAddress.roadAddress,
        recipient_address_detail: selectedDelivery === 'default' ? 
          deliveryData.recipient_address_detail : 
          deliveryAddress.detailAddress,
        recipient_memo: selectedMessage === 'reason6' ? 
          customMessage : 
          messageList.find(msg => msg.value === selectedMessage)?.name || ""
      },

      depositInfo: {
        deposit_refund_account: "",
        deposit_refund_mname: "",
        deposit_refund_name: "",
        deposit_refund_price: orderSummary?.totalInfo?.deposit_price || 0,
    },
      
      user_memo: memo,
      pay_select_goods: selectedProductIds.filter(id => {
        const isMainChecked = cartListData.bundleMain?.some(item => 
          item.cart_option_seq === id && item.isChecked);
        const isAddChecked = cartListData.bundleAdd?.some(item => 
          item.cart_option_seq === id && item.isChecked);
        const isAccChecked = cartListData.bundleAcc?.some(item => 
          item.cart_option_seq === id && item.isChecked);
        return isMainChecked || isAddChecked || isAccChecked;
      })
    };

    // 선택된 상품이 있는지 한번 더 확인
    if (paymentForm.pay_select_goods.length === 0) {
      setIsPopupLoginError(t('select_payment_product'));
      return;
    }

    const result = await PaymentService.requestPayment(paymentForm);

    if (result.success) {
      const data = result.data;

      // 0원 결제 처리 (문자열 "0"과 숫자 0 모두 처리)
      if (Number(data.price) === 0) {
        const zeroPaymentData = PaymentService.createZeroPaymentData(data);
        setIsPopupLoginError('주문이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
        navigate(`/order/orderComplete/${zeroPaymentData.orderSeq}`, { 
          state: { 
            order_seq: zeroPaymentData.orderSeq,
            settleprice: zeroPaymentData.settleprice,
            order_kind: 'sale'
          } 
        });
        return;
      }

      if (result.isMobile) {
        const paymentWindow = PaymentService.processMobilePayment(data, window.location.origin);
        if (!paymentWindow) {
          setIsPopupLoginError('결제창을 열 수 없습니다. 팝업 차단을 확인해주세요.');
        }
      } else {
        setIsPayment(true);
        
        setTimeout(() => {
          const iframe = document.getElementById('payment-container');
          if (iframe) {
            const paymentUrl = PaymentService.createPaymentUrl(data, window.location.origin);
            iframe.src = paymentUrl;
          } else {
            console.error('결제창 iframe을 찾을 수 없습니다.');
            setIsPayment(false);
          }
        }, 100);
      }
    } else {
      setIsPopupLoginError(result.message || '결제 요청에 실패했습니다.');
      setIsPayment(false);
    }
  };

  // 배송지 목록 조회
  useEffect(() => {
    const fetchDeliveryList = async () => {
      try {
        const response = await orderApi.getDefaultDeliveryInfo();
        setDeliveryData(response.data.data);
        
        if (response.data.data.default_flag === 'Y') {
          setSelectedDelivery('default');
        } else {
          setSelectedDelivery('new');
        }
      } catch (error) {
        console.error('배송지 목록 조회 중 오류:', error);
        setSelectedDelivery('new');
      }
    };
    fetchDeliveryList();
  }, []);

    // agree_flag 체크를 위한 useEffect (기존 fetchMyInfo API 호출 대신)
    useEffect(() => {
      if (memberTokens && userInfo?.agree_flag === 'R') {
        setIsPopupLoginError({
          message: t("authentication_info_expired"),
          action: 'navigate_profile_edit'
        });
      }
    }, [memberTokens, userInfo, t]);
  
    const handleConfirm = () => {
      if (isPopupLoginError.action === 'navigate_profile_edit') {
        navigate('/myPage/profile/edit');
      } else if (isPopupLoginError.action === 'navigate_login') {
        navigate('/login');
      }
    }

  // 팝업 상태 추가
  const [isTermsPopup, setIsTermsPopup] = useState(false);
  const [popupContent, setPopupContent] = useState({ title: '', content: '' });

  // 약관 보기 버튼 클릭 핸들러 수정
  const handleViewTerms = async (type, event) => {
    event.stopPropagation(); // 이벤트 전파 중지
    event.preventDefault(); // 기본 동작 방지
    
    let title = '';
    let dataKey = '';

    switch(type) {
      case 'order_product_agreement':
        title = '주문 상품에 동의';
        dataKey = 'order_product_agreement';
        break;
      case 'privacy_use_agreement':
        title = '개인정보 수집/이용 동의';
        dataKey = 'privacy_use_agreement';
        break;
      case 'privacy_3rd_agreement':
        title = '개인정보 제3자 제공 동의';
        dataKey = 'privacy_3rd_agreement';
        break;
      case 'pg_service_agreement':
          title = '';
          dataKey = 'pg_service_agreement';
          break;
      default:
        return;
    }

    try {
      const response = await instance.post(TERMS_LIST_URL);
      
      if (response.data.code === 200) {
        setPopupContent({ 
          title, 
          content: response.data.data[dataKey] || '약관 내용을 불러올 수 없습니다.' 
        });
        setIsTermsPopup(true);
      } else {
        throw new Error(response.data.message || '약관 내용을 불러올 수 없습니다.');
      }
    } catch (error) {
      console.error('약관 내용을 불러오는 중 오류 발생:', error);
      setPopupContent({ 
        title, 
        content: '약관 내용을 불러올 수 없습니다. 잠시 후 다시 시도해주세요.' 
      });
      setIsTermsPopup(true);
    }
  };
  
  // 대표 상담번호 
  const [storePhone, setStorePhone] = useState('');

  useEffect(() => {
    const fetchStorePhone = async () => {
      try {
        const response = await instance.post(FOOTER_DATA_URL);
        setStorePhone(response.data.data.data);
      } catch (error) {
        console.error('대표 상담번호 가져오기 실패:', error);
      }
    };
    fetchStorePhone();
  }, []);



  return(
    <div>
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
      <div className="order_inner">

        {/* 베송지 정보 */}
        <section style={{marginTop:'40px'}}>
          <div className="order_title">
            <div className="flex-8"> 
              <h3>{t('delivery_info')}</h3>
            </div>
            <div style={{font:'var(--body1-1)'}}>
              <span className="reset_text_btn">* </span>{t('required_fields')}
            </div>
          </div>
          <ul className="default_info_table addr_table">
            <li className="info_item addr_radio">
              <div className="label">
                <p>{t('select_delivery_address')} <span className="yellow">*</span></p>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  <div className="default_radio radio_list">
                    {deliveryLIst.map(item =>(
                      <div key={item.id}>
                        <input
                          type="radio"
                          name="selectDelivery"
                          id={item.id}
                          value={item.value}
                          checked={selectedDelivery === item.value}
                          onChange={handleDeliveryChange}
                          />
                        <label htmlFor={item.id}>{item.label}</label>
                      </div>
                    ))}

                    <button 
                      type="button" 
                      className="default_btn light_gray sm lang_sm"
                      style={{marginLeft:'4px'}}
                      onClick={handleAddrSelectPopup}
                    >
                      {t('view_address_book')}
                    </button>
                    </div>
                </div>
              </div>
            </li>

            {/* 받는 사람 */}
            <li className="info_item addr_receiver">
              <div className="label">
                <label htmlFor="receiver">
                  <p>{t('recipient')} <span className="yellow">*</span></p>
                </label>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  <div className="default_input none_class">
                    <input
                      id="receiver"
                      type="text"
                      value={selectedDelivery === 'default' ? 
                        deliveryData.recipient_name || '' : 
                        recipientInfo.name}
                      onChange={(e) => {
                        if (selectedDelivery === 'new') {
                          setRecipientInfo(prev => ({
                            ...prev,
                            name: e.target.value
                          }));
                        }
                      }}
                      disabled={selectedDelivery === 'default'}
                      placeholder={t('recipient_name')}
                    />
                  </div>
                </div>
              </div>
            </li>

            {/* 핸드폰 번호 */}
            <li className="info_item addr_receiver">
              <div className="label">
                <p>{t('phone_number')} <span className="yellow">*</span></p>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  <div className="default_input flex-center-8 none_class">
                    <input
                      type="text"
                      value={selectedDelivery === 'default' ? 
                        deliveryData.recipient_phone || '' : 
                        recipientInfo.phone}
                      onChange={(e) => {
                        if (selectedDelivery === 'new') {
                          setRecipientInfo(prev => ({
                            ...prev,
                            phone: e.target.value
                          }));
                        }
                      }}
                      disabled={selectedDelivery === 'default'}
                      placeholder={t('delivery_address_name3')}
                    />
                  </div>
                </div>
              </div>
            </li>

            {/* 주소 입력 */}
            <li className="info_item addr_edit">
              <div className="label">
                <p>{t('delivery_address')} <span className="yellow">*</span></p>
              </div>
              <div className="form_info">
                <div className="info_detail default_input">
                  <div className={`address_input ${selectedDelivery === 'default' ? 'readonly' : ''}`}>
                    <div className="zip_code flex-center-4">
                      <input 
                        type="text" 
                        value={selectedDelivery === 'default' ? 
                          deliveryData.recipient_zipcode || '' : 
                          deliveryAddress.zonecode}
                        readOnly 
                      />
                      <button 
                        type="button" 
                        className="default_btn sm light_gray"
                        onClick={handlePostcodeOpen}
                        disabled={selectedDelivery === 'default'}
                      >
                        {t('postal_code_search')}
                      </button>
                    </div>
                    <input 
                      type="text" 
                      value={selectedDelivery === 'default' ? 
                        deliveryData.recipient_address_street || '' : 
                        deliveryAddress.roadAddress}
                      readOnly 
                    />
                    <input 
                      type="text" 
                      placeholder={t('delivery_address_name9')} 
                      value={selectedDelivery === 'default' ? 
                        deliveryData.recipient_address_detail || '' : 
                        deliveryAddress.detailAddress}
                      onChange={(e) => setDeliveryAddress(prev => ({ 
                        ...prev, 
                        detailAddress: e.target.value 
                      }))}
                      disabled={selectedDelivery === 'default'}
                    />
                  </div>
                </div>
              </div>
            </li>

            <li className="info_item addr_edit">
              <div className="label">
                <p>{t('delivery_message')}</p>
              </div>
              <div className="form_info flex-wrap">
                <div className="info_detail">
                  <div className={`default_select border ${isMessageOpen ? 'open' : ''}`}>
                    <button
                      type="button"
                      className="select_btn"
                      onClick={() => setIsMessageOpen(!isMessageOpen)}
                    >
                      <span className="ellipsis_clamp">
                        {selectedMessage ? 
                          messageList.find(msg => msg.value === selectedMessage)?.name : 
                          t('delivery_message_txt')
                        }
                      </span>
                    </button>
                    {isMessageOpen && (
                      <ul className="select_list">
                        {messageList.map(msg => (
                          <li
                            key={msg.value}
                            className={`select_item ${selectedMessage === msg.value ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedMessage(msg.value);
                              setIsMessageOpen(false);
                            }}
                            
                          >
                            {msg.name}
                          </li>
                        ))}
                      </ul>
                    )}
                    {selectedMessage === 'reason6' && (
                      <div className="default_input w-100">
                        <textarea
                          value={customMessage}
                          onChange ={(e)=> setCustomMessage(e.target.value)}
                          maxLength={50}
                          placeholder={t('input_content_placeholder')}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </li>    
          </ul>

        </section>

        {/* 우편번호 모달 */}
        {isPostcodeOpen && (
          <>
            <div 
              className="dimmed" 
              onClick={handlePostcodeClose}
              style={{
                position: 'fixed',
                width: '100vw',
                height: '100vh',
                top: '0',
                left: '0',
                background: 'rgba(0, 0, 0, 0.50)',
                zIndex: 101
              }}
            />
            <div className="default_popup">
              <div className="modal_content">
                <div className="modal_wrap">
                  <div className="modal_header">
                    <button 
                      type="button" 
                      className="close_btn" 
                      onClick={handlePostcodeClose}
                    ></button>
                  </div>
                  <div className="modal_body">
                    <DaumPostcodeEmbed
                      onComplete={handlePostcodeComplete}
                      onClose={handlePostcodeClose}
                      style={{ height: '460px' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 상품 선택 */}
        <section className="mt_48 check_pd">
          <div className="order_title">
            <div className="flex-8"> 
              <h3>{t('select_product_txt')}</h3>
              <p className="count">
                {selectedProductIds.length} <span style={{color:"#8d8d8d"}}>/{cartListData?.bundleMain?.length || 0}</span>
              </p>
            </div>
            <div className="o_btn">
              <button
                type="button"
                className="default_btn black xs"
                onClick={handleSelectedDelete}
              >
                {t('delete_selected_product')}
              </button>
            </div>
          </div>
          <div>
            <ul className="rent order_prod_list">
              {cartListData?.bundleMain && cartListData.bundleMain.length > 0 ? (
                cartListData.bundleMain.map((item, index) => (
                  <li key={item.cart_option_seq || index}
                  style={{
                    borderTop: index > 0 ? '1px solid black' : 'none'
                  }}>
                    <OrderItem
                      item={item}
                      orderSummary={"sale"}
                      onSelect={(productId, isSelected) => handleProductSelect(productId, isSelected, 'main')}
                      onQuantityChange={handleProductQuantityChange}
                      onDelete={handleDelete}
                    />
                  </li>
                ))
              ) : (
                <li className="no_result">{t('no_product')}</li>
              )}
            </ul>

            {/* 총 상품 수량, 상품 금액 */}
            <div className="total_product flex-center-8">
              <div className="flex-center-2">
                {t('total_product_quantity2')}
                <p><span className="strong_b1">{orderSummary?.totalInfo?.total_rental_ea || 0}</span>{t('quantity_count_txt')}</p>
              </div>
              <div className="flex-center-2">
                {t('total_product_quantity4_0617')}
                <p className="cap2_1">₩<span className="strong_b1">
                  {formatLocalString(orderSummary?.totalInfo?.sumBundleMainPrice || 0)}
                </span></p>
              </div>
            </div>
          </div>
        </section>

        {/* 결제금액 */}
        <section className="mt_48">
          <div className="order_title">
            <h3>{t('payment_amount')}</h3>
          </div>

          <div>
            <OrderPayment 
              key={requestParams.key}
              orderSummary={orderSummary} 
              onUpdateParams={handleUpdateParams}
              orderType={"sale"}
              requestParams={requestParams}
            />
          </div>
        </section>

        {/* 요청사항 */}
        <section className="mt_48 container request_input">
          <div className="order_title">
            <h3>{t('request_txt')}</h3>
            <p className="text_small_b">{t('need_help_0617')}
              <span className="strong_b3 black">02-{storePhone?.csMainPhone?.value}</span>
              {t('need_help_0617_2')}
            </p>
          </div>
          <div className="default_input">
            <textarea 
              className="form_input"
              value={memo}
              onChange={handleMemoChange}
              placeholder={t('request_placeholder')}
            />
          </div>
        </section> 
      </div>


      {/* 결제정보표시 */}
      <div className="total_payment_con sale_payment">
        <div className="order_info_box">
          <div className="order_left">
            <div className="bottom_con">
              <div className=" join_con">
                <div className="default_chk_box all_chk">
                  <input 
                    type="checkbox" 
                    id="allChk" 
                    checked={isAllChecked}
                    onChange={handleAllCheck}
                  />
                  <label htmlFor="allChk">
                    <span>{t('order_info_check_0617')}</span>
                  </label>
                </div>  
                
                <ul className="flex-2 column g-90">
                  <li className="default_chk_box">
                    <input 
                      type="checkbox"
                      id="useAgree1"  
                      name="option1"
                      checked={isChecked.option1}
                      onChange={() => handleCheck('option1')}
                    />
                    <label htmlFor="useAgree1">
                      <span className="flex-btw">
                        {t('order_product_agree_0617')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('order_product_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input 
                      type="checkbox"
                      id="useAgree2"  
                      name="option2"
                      checked={isChecked.option2}
                      onChange={() => handleCheck('option2')}
                    />
                    <label htmlFor="useAgree2">
                      <span className="flex-btw">
                        {t('order_personal_info_agree_0617')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('privacy_use_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input 
                      type="checkbox" 
                      id="useAgree3" 
                      name="option3"
                      checked={isChecked.option3}
                      onChange={() => handleCheck('option3')}
                    />
                    <label htmlFor="useAgree3">
                      <span className="flex-btw">
                        {t('order_personal_info_agree_0617_2')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('privacy_3rd_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input 
                      type="checkbox" 
                      id="useAgree4" 
                      name="option4"
                      checked={isChecked.option4}
                      onChange={() => handleCheck('option4')}
                    />
                    <label htmlFor="useAgree4">
                      <span className="flex-btw">
                        {t('order_payment_service_agree_0617')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('pg_service_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="order_right">
            <div className="total_detail">
              <div className="total_count">
                <ul className="flex-2 column">
                  <li className="flex-btw"> 
                    {t('total_product_quantity_txt')}
                    <p>{orderSummary?.totalInfo?.total_ea}<span className="text_small">{t('quantity_count_txt')}</span></p>
                  </li>
                </ul>         
              </div>
        
            </div>
            <div className="total_amount"> 
              <div className="flex-btw price_box">
                <p className="label red">{t('total_payment_amount_0617')}</p>
                <p className="o_total_price red">
                  ₩<span>{formatLocalString(orderSummary?.totalInfo?.settlePrice || 0)}</span>
                </p> 
              </div> 
              <p 
                className="text_small_b g-70" 
                style={{textAlign:'right'}}
              >
                ₩{formatLocalString(orderSummary?.totalInfo?.settlePrice || 0)}{t('total_payment_amount_0617_2')}
              </p>
            </div>

            <div className="payment_btn">
              <button
                type="button"
                className="pay_yellow_btn won"
                onClick={handlePayment}
              >
                {formatLocalString(orderSummary?.totalInfo?.settlePrice || 0)} {t('pay_now')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AddressSelectPopup 
        isPopup={isPopupAddr} 
        setIsPopup={setPopupAddr} 
        deliveryData={deliveryData}
        onConfirm={handleAddressConfirm}
      />

      {/* 결제 모달 추가 */}
      {isPayment && (
        <div id="payment" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 102
        }}>
          {/* dimmed 배경 추가 */}
          <div className="dimmed" style={{
            position: 'fixed',
            width: '100vw',
            height: '100vh',
            top: 0,
            left: 0,
            background: 'rgba(0, 0, 0, 0.50)',
            zIndex: 101
          }} />
          {/* iframe 추가 */}
          <iframe
            id="payment-container"
            name="paymentIframe"
            title="이니시스 결제창"
            style={{
              width: '820px',
              height: '600px',
              position: 'relative',
              zIndex: 1000,
              border: 'none'
            }}
          />
        </div>
      )}

      <PopupLoginError
        isPopup={isPopupLoginError}
        setIsPopup={setIsPopupLoginError}
        onConfirm={handleConfirm}
      />
      
    {/* 약관 팝업 */}
    <TermsPopup 
      isPopup={isTermsPopup}
      setIsPopup={setIsTermsPopup}
      title={popupContent.title}
      content={popupContent.content}
    />
    </div>

  )
};
export default SaleOrder;